import { DatePipe, NgClass, NgStyle } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IonButton,
  IonIcon,
  IonModal,
  IonRange,
  IonRippleEffect,
  IonSkeletonText,
  RangeCustomEvent
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { cloneDeep, groupBy, toNumber } from 'lodash';
import { DateTime } from 'luxon';
import { concatMap, of } from 'rxjs';
import { selectAuthProfile } from '@components/auth/auth.selectors';
import { LWBHomeService } from '@components/lwb/home/home.service';
import { DailyJournalService } from '@pages/daily/journal/journal';
import { DailyJournalActions } from '@pages/daily/journal/journal.actions';
import {
  DailyJournalAnswer,
  DailyJournalQuestionResponseFormatOption,
  DailyJournalQuestionResponseFormatType,
  DailyJournalQuestionResponseType,
  DailyJournalQuestionResponseUnit,
  DailyJournalSettings,
  DailyJournalSummary,
  DailyJournalTypes
} from '@pages/daily/journal/journal.interface';
import {
  selectDailyJournalAnswer,
  selectDailyJournalErrors,
  selectDailyJournalFollowUpQuestions,
  selectDailyJournalLoading,
  selectDailyJournalSettings,
  selectDailyJournalSummary,
  selectDailyJournalTypes
} from '@pages/daily/journal/journal.selectors';
import { intialDailyJournalState } from '@pages/daily/journal/journal.state';
import { LodashGetPipe } from '@pipes/lodash-get/get.pipe';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';

/**
 * Questions Journal Modal Component.
 */
@Component({
  selector: 'app-questions-journal-modal',
  templateUrl: './questions-modal.component.html',
  styleUrls: ['./questions-modal.component.scss'],
  imports: [
    IonIcon,
    IonModal,
    DatePipe,
    IonButton,
    LodashGetPipe,
    NgClass,
    NgStyle,
    IonRange,
    IonRippleEffect,
    IonSkeletonText
  ],
  providers: [DailyJournalService],
  styles: [
    `
      ion-modal#questions-journal-modal {
        --width: 93%;
        --min-width: 250px;
        --max-height: 80%;
        --border-radius: 25px;
      }
    `
  ]
})
export class QuestionsJournalModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly events = inject(EventService);
  private readonly homeService = inject(LWBHomeService);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  private readonly questionsJournalModal = viewChild<IonModal>(
    'questionsJournalModal'
  );

  readonly closeModal = output();

  private readonly dailyJournalAnswer = signal<Array<DailyJournalAnswer>>(
    intialDailyJournalState().dailyJournalAnswer
  );
  private readonly dailyJournalSettings = signal<{
    count: number;
    results: Array<DailyJournalSettings>;
  }>(intialDailyJournalState().dailyJournalSettings);
  private readonly dailyJournalTypes = signal<Array<DailyJournalTypes>>(
    intialDailyJournalState().dailyJournalTypes
  );
  protected readonly date = signal<string>(
    DateTime.now().minus({ day: 1 }).toISODate()
  );
  private readonly loading = signal<{
    dailyJournalTypes: boolean;
    dailyJournalSettings: boolean;
  }>({
    dailyJournalTypes: false,
    dailyJournalSettings: false
  });
  protected readonly mappingAnswersByQuestionId = signal<
    Map<string, DailyJournalAnswer>
  >(new Map());
  private readonly organizedQuestionsWithAnswers = signal<{
    main: Array<DailyJournalAnswer>;
    followUp: Array<DailyJournalAnswer>;
  }>({
    main: [] as Array<DailyJournalAnswer>,
    followUp: [] as Array<DailyJournalAnswer>
  });
  protected readonly profileName = signal<string>('');
  private readonly selectedYesterday = signal<DailyJournalSummary | null>(null);

  readonly open = model<boolean>(false);

  readonly dailyJournalFollowUpQuestions = this.store.selectSignal(
    selectDailyJournalFollowUpQuestions
  );
  readonly dailyJournalQuestionResponseFormatType =
    DailyJournalQuestionResponseFormatType;

  readonly dailyJournalQuestionResponseType = DailyJournalQuestionResponseType;
  readonly dailyJournalQuestionResponseUnit = DailyJournalQuestionResponseUnit;

  readonly errors = this.store.selectSignal(selectDailyJournalErrors);

  readonly toNumber: typeof toNumber = toNumber;

  /**
   * @description Mapping of follow-up questions grouped by their main question IDs.
   */
  readonly mappingFollowUpQuestions = computed(() => {
    const followUpQuestions = this.dailyJournalFollowUpQuestions().results.map(
      (o) => {
        let defaultAnswer = null;

        if (
          o.responseType === DailyJournalQuestionResponseType.TIME_SELECTION
        ) {
          defaultAnswer = this.setDefaultValueTimeSelection().answer;
        } else if (
          o.responseType === DailyJournalQuestionResponseType.INPUT_NUMBER
        ) {
          let interval = 1;
          if (
            o.responseUnit &&
            o.responseUnit === this.dailyJournalQuestionResponseUnit.Minutes
          ) {
            interval = 5;
          }
          defaultAnswer = interval;
        }

        return {
          id: o.id,
          answer: defaultAnswer,
          dailyJournalQuestionId: o.id,
          dailyJournalQuestion: o,
          createdAt: '',
          updatedAt: ''
        };
      }
    );
    const groupByMainQuestion = groupBy(
      followUpQuestions,
      'dailyJournalQuestion.mainQuestionId'
    );
    const followUpQuestionsMap = new Map();

    // Iterate through grouped follow-up questions and populate the map
    for (const [key, value] of Object.entries(groupByMainQuestion)) {
      followUpQuestionsMap.set(key, value);
    }

    return followUpQuestionsMap;
  });

  /**
   * @description Mapping of questions grouped by their types along with answers.
   */
  readonly mappingQuestionsAndAnswers = computed(() => {
    const types = this.dailyJournalTypes();
    const questions = this.organizedQuestionsWithAnswers();

    // Create a map for faster type lookups
    const typeMap = new Map(types.map((type) => [type.id, type]));
    // Group questions by type ID
    const groupedByType = new Map<string, typeof questions.main>();

    for (const question of questions.main) {
      const typeId = question.dailyJournalQuestion.dailyJournalTypeId;
      if (!typeId) continue;
      if (!groupedByType.has(typeId)) {
        groupedByType.set(typeId, []);
      }
      groupedByType.get(typeId)?.push(question);
    }

    // Convert to array and sort by position
    const result = Array.from(groupedByType.entries())
      .map(([typeId, questions]) => ({
        dailyJournalType: typeMap.get(typeId) ?? null,
        questions
      }))
      .sort((a, b) => {
        const posA = a.dailyJournalType?.position ?? 0;
        const posB = b.dailyJournalType?.position ?? 0;
        return posA - posB;
      });
    return result;
  });

  readonly questionsError = computed(
    () =>
      this.errors().dailyJournalQuestions ||
      this.errors().dailyJournalFollowUpQuestions
  );

  /**
   * @description Get daily journal answers from the store.
   */
  constructor() {
    effect(() => {
      const answers = this.dailyJournalAnswer();
      const questions = this.dailyJournalSettings().results.map((o) => {
        let defaultAnswer = null;

        if (
          o.dailyJournalQuestion.responseType ===
          DailyJournalQuestionResponseType.TIME_SELECTION
        ) {
          defaultAnswer = this.setDefaultValueTimeSelection().answer;
        } else if (
          o.dailyJournalQuestion.responseType ===
          DailyJournalQuestionResponseType.INPUT_NUMBER
        ) {
          defaultAnswer = 0;
        } else if (
          o.dailyJournalQuestion.responseType ===
          DailyJournalQuestionResponseType.CUSTOM_VARIABLE
        ) {
          if (
            o.dailyJournalQuestion.responseFormat?.typeCustomVariable ===
            DailyJournalQuestionResponseFormatType.SLIDER
          ) {
            const defaultValue = this.getValueInputRangeHandler(
              o.dailyJournalQuestion.responseFormat.options,
              null
            );
            defaultAnswer = defaultValue?.answer;
          }
        }

        return {
          ...o,
          answer: defaultAnswer
        };
      });
      const _answers = new Set(answers.map((o) => o.dailyJournalQuestionId));
      const _questions = questions.filter(
        (o) => !_answers.has(o.dailyJournalQuestionId)
      );
      const result = [...answers, ..._questions];

      const q = {
        main: result.filter(
          (o) => o.dailyJournalQuestion.isFollowUp === false
        ) as Array<DailyJournalAnswer>,
        followUp: result.filter(
          (o) => o.dailyJournalQuestion.isFollowUp === true
        ) as Array<DailyJournalAnswer>
      };

      this.organizedQuestionsWithAnswers.set(q);
    });

    effect(() => {
      const answers = this.dailyJournalAnswer();

      this.mappingAnswersByQuestionId.set(
        new Map(
          answers.map((answer) => [answer.dailyJournalQuestionId, answer])
        )
      );
    });
  }

  /**
   * @description Lifecycle hook that is called after data-bound properties are initialized.
   */
  ngOnInit(): void {
    this.store
      .select(selectDailyJournalTypes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((types) => {
        this.dailyJournalTypes.set(types);
      });

    this.store
      .select(selectDailyJournalSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        this.dailyJournalSettings.set(settings);
      });

    this.store
      .select(selectDailyJournalLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((load) => {
        this.loading.set({
          dailyJournalTypes: load.dailyJournalTypes,
          dailyJournalSettings: load.dailyJournalSettings
        });
      });

    this.store
      .select(selectDailyJournalSummary)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((summary) => {
        const yesterdaySummary = summary.length > 0 ? summary[0] : null;
        this.selectedYesterday.set(yesterdaySummary);

        this.fetchAnswer();
      });

    this.store
      .select(selectDailyJournalAnswer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answers) => {
        this.dailyJournalAnswer.set(answers);
      });

    this.store
      .select(selectAuthProfile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const profile = cloneDeep(value);

        let profileName = '';

        if (profile?.firstName) {
          profileName = profile.firstName ?? '';
        } else if (profile?.name) {
          profileName = profile.name ?? '';
        }

        this.profileName.set(
          profileName.length > 0 ? profileName.split(' ')[0] : ''
        );
      });
  }

  /**
   * @description Fetch initial data for the page.
   */
  fetch(): void {
    if (!this.session.isAuthenticated()) return;

    if (this.dailyJournalTypes().length === 0) {
      this.store.dispatch(DailyJournalActions.fetchDailyJournalTypes());
    }

    if (this.dailyJournalFollowUpQuestions().count === 0) {
      this.store.dispatch(
        DailyJournalActions.fetchDailyJournalFollowUpQuestions({
          params: {
            isFollowUp: true,
            limit: 100,
            offset: 0
          }
        })
      );
    }

    this.store.dispatch(
      DailyJournalActions.fetchDailyJournalSettings({
        params: {
          offset: 0,
          limit: 100,
          includeQuestionDetail: true
        }
      })
    );

    this.fetchSummary();
  }

  /**
   * @description Fetch answer for a specific daily journal entry.
   */
  fetchAnswer(): void {
    const yesterday = this.selectedYesterday();

    if (yesterday?.userDailyJournalId) {
      this.store.dispatch(
        DailyJournalActions.fetchDailyJournalAnswer({
          userDailyJournalId: yesterday.userDailyJournalId,
          params: { includeQuestionDetail: true }
        })
      );
    } else {
      this.store.dispatch(
        DailyJournalActions.setDailyJournalAnswer({ dailyJournalAnswer: [] })
      );
    }
  }

  /**
   * @description Fetch summary data for the selected day.
   */
  fetchSummary(): void {
    const params = {
      startDate: DateTime.fromISO(this.date()).toFormat('yyyy-MM-dd'),
      endDate: DateTime.fromISO(this.date()).toFormat('yyyy-MM-dd')
    };
    this.store.dispatch(
      DailyJournalActions.fetchDailyJournalSummary({
        params
      })
    );
  }

  /**
   * @description Handle "Do This Later" action.
   */
  doThisLater(): void {
    this.open.set(false);
    this.closeModal.emit();
    this.store.dispatch(
      DailyJournalActions.setDailyJournalAnswer({
        dailyJournalAnswer: []
      })
    );
  }

  /**
   * @description Check FTUE availability for Daily Journal feature.
   * @param {Array<DailyJournalQuestionResponseFormatOption>} options Options for the response format.
   * @param {(DailyJournalQuestionResponseFormatOption | null)} answer The current answer.
   * @returns {({
   *     index: number | null;
   *     option: DailyJournalQuestionResponseFormatOption;
   *   })}
   */
  getValueInputRangeHandler(
    options: Array<DailyJournalQuestionResponseFormatOption>,
    answer: DailyJournalAnswer['answer']
  ): {
    index: number;
    answer: DailyJournalQuestionResponseFormatOption;
  } | null {
    if (typeof answer === 'object' && answer && options.length > 0) {
      const _answer = answer as DailyJournalQuestionResponseFormatOption;
      const index = options.findIndex((opt) => opt.value === _answer.value);
      if (index !== -1) {
        return {
          index: index + 1, // +1 because range starts from 1
          answer: options[index]
        };
      }
    } else if (options.length > 0) {
      const defaultOption = options.find((opt) => opt.isDefault);
      if (defaultOption) {
        const defaultIndex = options.findIndex(
          (opt) => opt.value === defaultOption.value
        );
        if (defaultIndex !== -1) {
          return {
            index: defaultIndex + 1, // +1 because range starts from 1
            answer: options[defaultIndex]
          };
        }
      } else {
        return {
          index: Math.ceil(options.length / 2),
          answer: options[Math.ceil(options.length / 2) - 1]
        };
      }
    }
    return null;
  }

  /**
   * @description Set the answer for a specific daily journal question.
   * @param {DailyJournalAnswer['answer']} answer The answer to set.
   * @returns {{
   *     index: number;
   *     minutes: number;
   *     answer: string;
   *   }}
   */
  getValueIonRangeHandler(answer: DailyJournalAnswer['answer']): {
    index: number;
    minutes: number;
    answer: string;
  } {
    // Handle time string or provide default
    if (typeof answer === 'string' && answer) {
      const [hour, minute] = answer.split(':').map(Number);
      if (!isNaN(hour) && !isNaN(minute)) {
        const totalMinutes = hour * 60 + minute;
        return {
          index: Math.ceil(totalMinutes / 30),
          minutes: totalMinutes,
          answer
        };
      }
    }

    return this.setDefaultValueTimeSelection();
  }

  /**
   * @description Handle input range changes for exercise level selection.
   * @param {Event} event Event object from the input change.
   * @param {Array<DailyJournalQuestionResponseFormatOption>} options Options for the response format.
   * @param {string} question Daily journal question.
   */
  onChangeInputRangeHandler(
    event: Event,
    options: Array<DailyJournalQuestionResponseFormatOption>,
    question: DailyJournalAnswer
  ): void {
    const target = event.target as HTMLInputElement;
    const value = target.valueAsNumber || toNumber(target.value);

    this.setValueQuestionAnswer(
      question,
      options[value - 1],
      DailyJournalQuestionResponseType.CUSTOM_VARIABLE,
      true
    );
  }

  /**
   * @description Handle changes to the ion-range component for time selection.
   * @param {RangeCustomEvent} event Event containing the new range value.
   * @param {string} question Daily journal question.
   */
  onChangeIonRangeForTimeSelectionHandler(
    event: RangeCustomEvent,
    question: DailyJournalAnswer
  ): void {
    const value = toNumber(event.detail.value || 0);
    const hours = Math.floor((value * 30) / 60);
    const minutes = (value * 30) % 60;
    const timeValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    this.setValueQuestionAnswer(
      question,
      value >= 47 ? '23:30' : timeValue,
      DailyJournalQuestionResponseType.TIME_SELECTION,
      true
    );
  }

  /**
   * @description Handle click on the title of an input range option.
   * @param {number} index Index of the clicked option.
   * @param {Array<DailyJournalQuestionResponseFormatOption>} options Options for the response format.
   * @param {string} question Daily journal question.
   * @returns {void}
   */
  onClickTitleInputRangeHandler(
    index: number,
    options: Array<DailyJournalQuestionResponseFormatOption>,
    question: DailyJournalAnswer
  ): void {
    if (options.length === 0) return;
    const selectedOption = options[index];
    this.setValueQuestionAnswer(
      question,
      selectedOption,
      DailyJournalQuestionResponseType.CUSTOM_VARIABLE,
      true
    );
  }

  /**
   * On did dismiss modal.
   */
  onDidDismissModal(): void {
    this.store.dispatch(
      DailyJournalActions.setDailyJournalAnswer({
        dailyJournalAnswer: []
      })
    );
    this.mappingAnswersByQuestionId.set(new Map());

    const modal = this.questionsJournalModal();
    if (modal) {
      modal.showBackdrop = true;
    }
  }

  /**
   * Save user consent after modal present.
   */
  onDidPresentModal(): void {
    this.saveUserConsent();
    this.fetch();
  }

  /**
   * On will dismiss modal.
   */
  onWillDismissModal(): void {
    const modal = this.questionsJournalModal();
    if (modal) {
      modal.showBackdrop = false;
    }
  }

  /**
   * @description Save user consent.
   */
  saveUserConsent(): void {
    of(true)
      .pipe(
        concatMap(() => {
          return this.homeService.saveUserConsent({
            date: this.date(),
            featureCode: 'DAILY_CHECK_IN'
          });
        })
      )
      .subscribe();
  }

  /**
   * @description Set default value for time selection to the nearest past 30-minute interval.
   * @returns {{
   *     index: number;
   *     minutes: number;
   *     answer: string;
   *   }}
   */
  setDefaultValueTimeSelection(): {
    index: number;
    minutes: number;
    answer: string;
  } {
    // Default to current time
    const now = DateTime.now();
    // Calculate total minutes since midnight
    const hour = now.hour;
    const minute = now.minute;
    const totalMinutes = hour * 60 + minute;

    // Round down to the nearest hour in the past
    // This ensures we never exceed the current time
    const roundedHour = Math.floor(totalMinutes / 60);
    const roundedMinute = 0;

    return {
      index: roundedHour * 2, // Multiply by 2 since index is based on 30-minute intervals
      minutes: roundedHour * 60, // Total minutes
      answer: `${roundedHour.toString().padStart(2, '0')}:${roundedMinute.toString().padStart(2, '0')}`
    };
  }

  /**
   * @description Set the value of an answer for a specific question.
   * @param {string} question Question.
   * @param {(boolean | string)} value Value to set.
   * @param {string} responseType Response type of the question.
   * @param {boolean} updateUseSignal Whether to update using signal. If true, updates the signal state; otherwise, updates the map directly.
   */
  setValueQuestionAnswer(
    question: DailyJournalAnswer,
    value: DailyJournalAnswer['answer'],
    responseType: DailyJournalQuestionResponseType,
    updateUseSignal = false
  ): void {
    const existingAnswer = this.mappingAnswersByQuestionId().get(
      question.dailyJournalQuestionId
    );

    if (typeof value === 'string' && value === 'increment') {
      if (responseType === DailyJournalQuestionResponseType.INPUT_NUMBER) {
        let interval = 1;
        let maximum = 100;
        if (
          question.dailyJournalQuestion.responseUnit &&
          question.dailyJournalQuestion.responseUnit ===
            this.dailyJournalQuestionResponseUnit.Minutes
        ) {
          interval = 5;
          maximum = 60;
        } else if (
          question.dailyJournalQuestion.responseUnit &&
          question.dailyJournalQuestion.responseUnit ===
            this.dailyJournalQuestionResponseUnit.Hours
        ) {
          maximum = 24;
        }
        if (existingAnswer) {
          this.mappingAnswersByQuestionId.update((prev) => {
            const updatedMap = new Map(prev);
            updatedMap.set(question.dailyJournalQuestionId, {
              ...existingAnswer,
              answer:
                Number(existingAnswer.answer) + interval >= maximum
                  ? maximum
                  : Number(existingAnswer.answer) + interval
            });
            return updatedMap;
          });
        } else {
          this.mappingAnswersByQuestionId.update((prev) => {
            const updatedMap = new Map(prev);
            updatedMap.set(question.dailyJournalQuestionId, {
              ...question,
              answer: isNaN(Number(question.answer))
                ? interval
                : Number(question.answer) + interval
            });
            return updatedMap;
          });
        }
      }
    } else if (typeof value === 'string' && value === 'decrement') {
      if (responseType === DailyJournalQuestionResponseType.INPUT_NUMBER) {
        let interval = 1;
        if (
          question.dailyJournalQuestion.responseUnit &&
          question.dailyJournalQuestion.responseUnit ===
            this.dailyJournalQuestionResponseUnit.Minutes
        ) {
          interval = 5;
        }
        if (existingAnswer) {
          this.mappingAnswersByQuestionId.update((prev) => {
            const updatedMap = new Map(prev);
            updatedMap.set(question.dailyJournalQuestionId, {
              ...existingAnswer,
              answer:
                Number(existingAnswer.answer) - interval <= 0
                  ? 0
                  : Number(existingAnswer.answer) - interval
            });
            return updatedMap;
          });
        } else {
          this.mappingAnswersByQuestionId.update((prev) => {
            const updatedMap = new Map(prev);
            updatedMap.set(question.dailyJournalQuestionId, {
              ...question,
              answer: 0
            });
            return updatedMap;
          });
        }
      }
    } else if (existingAnswer) {
      if (updateUseSignal) {
        this.mappingAnswersByQuestionId.update((prev) => {
          const updatedMap = new Map(prev);
          updatedMap.set(question.dailyJournalQuestionId, {
            ...existingAnswer,
            answer: value
          });
          return updatedMap;
        });
      } else {
        this.mappingAnswersByQuestionId().set(question.dailyJournalQuestionId, {
          ...existingAnswer,
          answer: value
        });
      }
    } else {
      if (updateUseSignal) {
        this.mappingAnswersByQuestionId.update((prev) => {
          const updatedMap = new Map(prev);
          updatedMap.set(question.dailyJournalQuestionId, {
            ...question,
            answer: value
          });
          return updatedMap;
        });
      } else {
        this.mappingAnswersByQuestionId().set(question.dailyJournalQuestionId, {
          ...question,
          answer: value
        });
      }
    }
  }

  /**
   * @description Submit the daily journal check-in.
   */
  submitDailyJournal(): void {
    const isValid = this.validationSubmitDailyJournal();
    if (!isValid) {
      this.events.publish(this.events.keys.TOAST_ERROR, {
        message: 'Please complete your check-in first',
        color: 'toast-failed',
        duration: 2000,
        cssClass: 'ion-toast-text-white'
      });
      return;
    }

    const listOfAnswer = [];

    const questions = this.organizedQuestionsWithAnswers();
    const answer = this.mappingAnswersByQuestionId();
    const followUpQuestions = this.mappingFollowUpQuestions();

    for (const q of questions.main) {
      const questionAnswer = answer.get(q.dailyJournalQuestionId);

      // Ensure main question is answered
      if (questionAnswer === undefined) {
        continue;
      }

      // Add main question answer
      listOfAnswer.push({
        dailyJournalQuestionId: questionAnswer.dailyJournalQuestion.id,
        answer: questionAnswer.answer
      });

      // If main question is boolean and true, ensure follow-up questions are answered
      if (
        questionAnswer.dailyJournalQuestion.responseType ===
          this.dailyJournalQuestionResponseType.BOOLEAN &&
        questionAnswer.answer === true
      ) {
        const fq = followUpQuestions.get(q.dailyJournalQuestionId);

        if (fq && fq.length > 0) {
          for (const f of fq) {
            const fAnswer = answer.get(f.dailyJournalQuestionId);

            // Ensure follow-up question is answered
            if (fAnswer === undefined) {
              continue;
            }

            listOfAnswer.push({
              dailyJournalQuestionId: fAnswer.dailyJournalQuestion.id,
              answer: fAnswer.answer
            });
          }
        }
      }
    }

    this.store.dispatch(
      DailyJournalActions.saveDailyJournalCheckin({
        payload: {
          date: DateTime.fromISO(this.date()).toFormat('yyyy-MM-dd'),
          listOfAnswer
        }
      })
    );
    this.open.set(false);
    this.store.dispatch(
      DailyJournalActions.setDailyJournalAnswer({
        dailyJournalAnswer: []
      })
    );
    this.mappingAnswersByQuestionId.set(new Map());
    this.closeModal.emit();
  }

  /**
   * @description Validate if the daily journal can be submitted.
   * @returns {boolean}
   */
  validationSubmitDailyJournal(): boolean {
    const questions = this.organizedQuestionsWithAnswers();
    const answer = this.mappingAnswersByQuestionId();
    const followUpQuestions = this.mappingFollowUpQuestions();

    let isValid = true;

    parentLoop: for (const q of questions.main) {
      const questionAnswer = answer.get(q.dailyJournalQuestionId);
      if (questionAnswer) {
        if (
          questionAnswer.dailyJournalQuestion.responseType ===
            this.dailyJournalQuestionResponseType.BOOLEAN &&
          questionAnswer.answer === true
        ) {
          const fq = followUpQuestions.get(q.dailyJournalQuestionId);
          if (fq && fq.length > 0) {
            for (const f of fq) {
              const fAnswer = answer.get(f.dailyJournalQuestionId);
              if (
                f.dailyJournalQuestion.responseType ===
                  DailyJournalQuestionResponseType.TIME_SELECTION ||
                f.dailyJournalQuestion.responseType ===
                  DailyJournalQuestionResponseType.INPUT_NUMBER
              ) {
                if (fAnswer === undefined) {
                  this.mappingAnswersByQuestionId.update((prev) => {
                    const updatedMap = new Map(prev);
                    updatedMap.set(f.dailyJournalQuestionId, f);
                    return updatedMap;
                  });
                }
                continue; // pass validation for time selection and input number
              } else if (fAnswer === undefined) {
                isValid = false;
                break parentLoop;
              } else {
                isValid = true;
              }
            }
          } else {
            isValid = true;
          }
        } else {
          isValid = true;
        }
      } else if (
        q.dailyJournalQuestion.responseFormat?.typeCustomVariable ===
        DailyJournalQuestionResponseFormatType.SLIDER
      ) {
        // Set default value for slider if not answered
        const defaultValue = this.getValueInputRangeHandler(
          q.dailyJournalQuestion.responseFormat.options,
          null
        );
        this.mappingAnswersByQuestionId.update((prev) => {
          const updatedMap = new Map(prev);
          updatedMap.set(q.dailyJournalQuestionId, {
            ...(q as unknown as DailyJournalAnswer),
            answer: defaultValue?.answer ?? null
          });
          return updatedMap;
        });
        isValid = true;
      } else {
        isValid = false;
        break;
      }
    }

    return isValid;
  }
}
